
import { get as getCookie } from 'js-cookie';
import { WebClientResponse, WebClientSuccessResponse, WebClientErrorResponse, WebClientError } from './webClientResponse';
import { WebClientRequester } from './webClientRequester';
import { WebClientRequest } from './webClientRequest';

const XSRF_TOKEN_COOKIE_KEY = 'XSRF-TOKEN';
const XSRF_TOKEN_HEADER_KEY = 'X-XSRF-TOKEN';

export const VIZPORTAL_API_PATH    = '/vizportal/api/web/v1';

/**
 * Implementation of a WebClientRequester that uses the fetch API protocols
 */
export class WebClientFetchRequester implements WebClientRequester {

  public constructor(
    private vizPortalWebClientApiPrefix: string = `${VIZPORTAL_API_PATH}`,
    private hostname: string = '',
    private xsrfToken: string = '',
    private authorizationToken: string | undefined = undefined
  ) {
    if (typeof fetch === 'undefined' || typeof Request === 'undefined' || typeof Response === 'undefined') {
      throw new Error('The fetch API is not defined in this browser. Try adding a polyfill library like "whatwg-fetch"');
    }
    if (typeof Promise === 'undefined') {
      throw new Error('Promise is not defined in this browser. Try adding a polyfill library like "es6-promise"');
    }
  }

  public send<TResponse>(webClientRequest: WebClientRequest<Object|void, TResponse>): PromiseLike<WebClientFetchResponse<TResponse>> {
    const path = `${this.hostname}${this.vizPortalWebClientApiPrefix}/${webClientRequest.method.name}`;

    const init: RequestInit = {
      method: 'POST',
      headers: this.getHeaders(),
      cache: 'default',
      mode: 'cors',
      credentials: 'same-origin',
      body: JSON.stringify({
        method: webClientRequest.method.name,
        params: webClientRequest.params
      })
    };

    let req = new Request(path, init);

    return fetch(req)
      .then<WebClientFetchResponse<TResponse>>((res: Response) => {
        return res
          .json()
          .then(
            (response: TResponse): WebClientSuccessResponse<TResponse> & FetchResponse => (
              {
                response,
                fetchRequest: req,
                fetchResponse: res
              }
            ),
            (error: Object): WebClientErrorResponse & FetchResponse => (
              {
                error: this.responseToWebClientError(res, error),
                fetchRequest: req,
                fetchResponse: res
              }
            )
          );
      });
  }

  private responseToWebClientError(res: Response, error: Object): WebClientError {
    // TODO: Replace with local error codes
    switch (res.status) {
      case 400: return { code: 400, error };
      default:  return { code: 1, message: 'Unknown error', error };
    }
  }

  private getXsrfToken(): string {
    return WebClientFetchRequester.getXsrfToken();
  }

  private getHeaders(): Headers {
    const baseHeaders: Record<string, string> = {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      [XSRF_TOKEN_HEADER_KEY]: this.xsrfToken == '' ? this.getXsrfToken() : this.xsrfToken,
    };

    if (this.authorizationToken) {
      return new Headers({
        ...baseHeaders,
        'Authorization': this.authorizationToken,
      });
    } else {
      return new Headers(baseHeaders);
    }
  }

  public static getXsrfToken(): string {
    return getCookie(XSRF_TOKEN_COOKIE_KEY);
  }
}

export interface FetchResponse {
  fetchRequest: Request;   // fetch API request
  fetchResponse: Response; // fetch API response
}

// Add FetchResponse to the WebClientResponse for debugging
export type WebClientFetchResponse<TOut> = WebClientResponse<TOut> & FetchResponse;
